import { useState, useRef, useEffect, useCallback } from 'react'
import { useSession } from '../../hooks/useSession'
import { api } from '../../services/api'
import { MessageRenderer } from './MessageRenderer'
import { TalkToSomeone } from './TalkToSomeone'
import { CouplesFloor } from './CouplesFloor'
import { useSettings } from '../../hooks/useSettings'
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll'
// The companion's voice lives in services/speech.js — one implementation shared
// by AI Session, Couples and Reflections. It used to be defined here, which is
// how it nearly became three slightly different answers to "is a long summary
// spoken in full?".
import { audioFromBase64, playChunkSequence, speakLong, speakText } from '../../services/speech'
// Entry rules (Couples pre-beta remediation): mode is chosen BEFORE the session
// starts; a couples session never fetches, shows or speaks a personalised
// greeting; the mode cannot change once a session exists. Pure + unit-tested.
import { planEntry, canChangeMode, canOfferReflectionPromotion, COUPLES_ENTRANCE, INDIVIDUAL_DEFAULT_GREETING } from '../../services/sessionEntry'

// Individual/Couples mode switch + in-session treatment suggestions — OFF unless
// enabled at build time (matches the backend FEATURE_SESSION_MODES gate).
const FEATURE_SESSION_MODES = import.meta.env.VITE_FEATURE_SESSION_MODES === 'true'
// Couples → Individual Reflection promotion: OFF for beta wave 1 (no durable
// server-side record of both partners' consent yet). Server refuses it too.
const FEATURE_COUPLES_REFLECTION_PROMOTION = import.meta.env.VITE_FEATURE_COUPLES_REFLECTION_PROMOTION === 'true'
// Mirrors the App.jsx gate — the Reflections pill only shows when the
// Reflections surface actually ships in this build.
const FEATURE_AMBIENT = import.meta.env.VITE_FEATURE_AMBIENT === 'true'

// Organic restyle (Board D2/M2, 2026-08-13): sage mode pills & chips, sage
// resume card. Structure and behavior untouched — values only.
const ui = {
  modeBar:    { display:'flex', gap:'0.5rem', justifyContent:'center', alignItems:'center', padding:'0.6rem 0 0.1rem', flexShrink:0 },
  modeBtn:    { border:'none', background:'var(--og-n100,#f9f4ed)', color:'var(--og-n700,#645c50)', borderRadius:'999px', padding:'0.45rem 1.1rem', fontSize:'0.82rem', cursor:'pointer', fontWeight:600, fontFamily:'inherit' },
  modeBtnOn:  { background:'var(--og-accent2,#7a8a5e)', color:'#fff' },
  modeCaption:{ textAlign:'center', fontSize:'0.74rem', color:'var(--og-n500,#a19786)', margin:'0.3rem 0 0.2rem' },
  chipRow:    { display:'flex', flexWrap:'wrap', gap:'0.45rem', marginTop:'0.6rem' },
  chip:       { border:'none', background:'var(--og-s100,#f0fae1)', color:'var(--og-s800,#3d472b)', borderRadius:'999px', padding:'0.42rem 0.85rem', fontSize:'0.78rem', cursor:'pointer', fontWeight:600, fontFamily:'inherit' },
  sayAloud:   { marginTop:'0.55rem', border:'1.5px solid var(--og-n300,#dcd3c4)', background:'transparent', color:'var(--og-n700,#645c50)', borderRadius:'999px', padding:'0.3rem 0.75rem', fontSize:'0.74rem', cursor:'pointer', fontFamily:'inherit' },
  speaker:    { display:'block', fontSize:'0.62rem', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:700, color:'var(--og-n500,#a19786)', marginBottom:'0.25rem' },
  resumeCard: { width:'100%', maxWidth:360, background:'var(--og-s100,#f0fae1)', border:'none', borderRadius:'22px', padding:'1.1rem 1.2rem', marginBottom:'1.25rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  resumeLead: { margin:0, fontSize:'0.92rem', lineHeight:1.55, color:'var(--og-s900,#272e1b)', fontWeight:600 },
  resumeSub:  { margin:0, fontSize:'0.8rem', lineHeight:1.5, color:'var(--og-n700,#645c50)' },
  resumeRow:  { display:'flex', gap:'0.5rem', marginTop:'0.2rem' },
  resumeGo:   { flex:1, padding:'0.7rem 0.8rem', border:'none', borderRadius:'999px', background:'var(--og-accent2,#7a8a5e)', color:'#fff', fontSize:'0.9rem', fontWeight:600, fontFamily:'inherit', cursor:'pointer' },
  resumeNo:   { flex:'0 0 auto', padding:'0.7rem 1rem', border:'1.5px solid var(--og-n300,#dcd3c4)', borderRadius:'999px', background:'transparent', color:'var(--og-n700,#645c50)', fontSize:'0.9rem', fontFamily:'inherit', cursor:'pointer' },
}

// How many spoken turns pass before the companion answers OUT LOUD in couples.
// 2 = it speaks once both partners have had the floor, which is the rhythm of a
// real session: each person is heard, then the facilitator reflects. Speaking
// after every single turn makes it a third participant talking over a couple;
// never speaking makes it a transcript service. It always speaks the opening
// and the closing summary regardless, and "Say that aloud" is always available.
const COUPLES_SPEAK_EVERY = 2

// Strip the "[Name] " prefix before display — the label is for the companion's
// benefit and for the transcript, not something to read back at the couple.
const TURN_LABEL = /^\[([^\]]{1,24})\]\s*/

export function SessionView({ onSessionComplete, onNavigate, seed }) {
  const { settings } = useSettings()
  const {
    sessionId, messages, loading, sessionLoading, sessionError,
    crisis, careTier, startSession, sendMessage, endSession,
    mode, setMode,
    recoverable, resumeSession, discardRecoverable,
  } = useSession()

  const [input,        setInput]        = useState('')
  const [recording,    setRecording]    = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const [voiceError,   setVoiceError]   = useState(null)
  const [phase,        setPhase]        = useState('session')
  const [ending,       setEnding]       = useState(false)
  // Voice is on by default in BOTH modes. In couples the companion is the one
  // with the voice — the partners speak normally and are transcribed, and the
  // companion answers aloud at the turning points (see COUPLES_SPEAK_EVERY).
  // It used to sit silent here, which was the right call when it would have
  // spoken over every single turn; the floor control makes a real rhythm
  // possible, so silence is no longer the safe default — it's just absence.
  const [manualMute,   setManualMute]   = useState(false)
  const [greeting,     setGreeting]     = useState(null)  // personalised companion opening

  // ── Couples: who is in the room, and whose turn it is ────────────────────
  const [couplesNames, setCouplesNames] = useState(null)  // { a, b } once set up
  const [floorHolder,  setFloorHolder]  = useState(null)  // name of the last speaker
  // Which replies have actually been voiced. State rather than a ref because
  // the "Say that aloud" control has to disappear the moment it's used.
  const [spokenIds,    setSpokenIds]    = useState(() => new Set())
  const markSpoken = useCallback(
    id => setSpokenIds(prev => (prev.has(id) ? prev : new Set(prev).add(id))),
    [],
  )

  // Voice is muted if: user toggled mute, OR settings disable voice / force text-only
  const muted = manualMute || settings.textOnly || !settings.voiceEnabled

  const bottomRef        = useRef(null)
  const textareaRef      = useRef(null)
  const mediaRecorderRef = useRef(null)
  const chunksRef        = useRef([])
  const streamRef        = useRef(null)
  const ttsAudioRef      = useRef(null)
  const lastSpokenRef    = useRef(null)
  const speakTokenRef    = useRef(0)   // bumped to supersede an in-flight chunk sequence
  const voiceChoiceRef   = useRef(false) // true once the user has tapped the voice control
  const turnsRef         = useRef(0)     // couples: spoken turns since it last answered aloud

  const wakeLockRef      = useRef(null)
  const seededRef        = useRef(false)   // one-shot: seed from an Ambient debrief

  useChatAutoScroll(bottomRef, messages)   // follow only while the reader is at the bottom

  // Switching mode mid-session resets the couples turn rhythm so the companion
  // doesn't inherit a half-counted exchange from the other mode. Voice itself
  // is on by default in both modes now, so there is no default to re-apply.
  useEffect(() => { turnsRef.current = 0 }, [mode])

  // ── Play audio from response (preloaded so voice tracks the text) ───────
  useEffect(() => {
    const last = messages[messages.length - 1]
    if (!last || last.role !== 'assistant') return
    if (muted) return
    if (last.id === lastSpokenRef.current) return

    // COUPLES RHYTHM: the companion speaks at the turning points, not after
    // every utterance. It always voices the opening (nothing said yet) and
    // anything during the close, and otherwise waits until both partners have
    // had the floor. Anything it passes over is still one tap from being heard.
    if (mode === 'couples') {
      // The "opening" lasts until BOTH partners have taken the floor at least
      // once. The old rule (assistant messages <= 1) went silent from the
      // second reply onward, which in the first live couples session (14 Aug)
      // meant the who's-here check-in appeared as text but was never spoken —
      // only one partner had spoken, so the every-2-turns rhythm hadn't
      // legitimately begun. Until both are in the room, every reply is voiced.
      const speakers = new Set()
      for (const m of messages) {
        if (m.role !== 'user') continue
        const lab = TURN_LABEL.exec(m.content || '')
        if (lab) speakers.add(lab[1])
      }
      const isOpening = speakers.size < 2
      const isClosing = phase !== 'session'
      if (!isOpening && !isClosing && turnsRef.current < COUPLES_SPEAK_EVERY) return
      turnsRef.current = 0
    }

    lastSpokenRef.current = last.id
    markSpoken(last.id)

    // If response included audio, decode and play it as soon as it can play
    // through, then continue with any remaining chunks of a long reply.
    if (last.audio) {
      try {
        const first = audioFromBase64(last.audio, last.mimeType)
        const token = ++speakTokenRef.current
        playChunkSequence(
          first,
          Array.isArray(last.speakRest) ? last.speakRest : [],
          ttsAudioRef,
          () => speakTokenRef.current !== token,
        ).catch(() => {})
        return
      } catch {}
    }
    // Fallback: fetch audio separately (only if response didn't include it)
    speakText(last.content, ttsAudioRef)
  }, [messages, muted, mode, phase])

  // Voice a message the companion delivered silently (couples between turning
  // points). Uses the full-length chunked path, so a summary is spoken whole.
  const sayAloud = useCallback((msg) => {
    if (!msg?.content) return
    markSpoken(msg.id)
    lastSpokenRef.current = msg.id
    const token = ++speakTokenRef.current
    speakLong(msg.content, ttsAudioRef, () => speakTokenRef.current !== token).catch(() => {})
  }, [markSpoken])

  // Stop any in-flight spoken chunks the moment voice is muted or turned off.
  useEffect(() => {
    if (!muted) return
    speakTokenRef.current++
    ttsAudioRef.current?.pause()
  }, [muted])

  // ...and on unmount, so speech never outlives the screen.
  useEffect(() => () => {
    speakTokenRef.current++
    ttsAudioRef.current?.pause()
  }, [])

  // ── Speak the greeting when it arrives ───────────────────────────────────
  useEffect(() => {
    if (!greeting || muted) return
    speakText(greeting, ttsAudioRef)
  }, [greeting, muted])

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 140) + 'px'
  }, [])
  useEffect(() => { autoResize() }, [input, autoResize])

  // ── Fetch personalised companion greeting on session start ────────────────
  const handleStartSession = async () => {
    // Mode is decided HERE, before anything context-sensitive exists. A couples
    // session gets the neutral entrance and never asks the server for the
    // personalised (memory-based) greeting — not shown, not spoken, not built.
    const plan = planEntry(mode)
    setGreeting(plan.greeting)              // couples: neutral line; individual: cleared until fetched
    const id = await startSession()
    if (!id || !plan.fetchPersonalisedGreeting) return
    try {
      const res = await api.get('/session/greet')
      if (res?.greeting) setGreeting(res.greeting)
    } catch { /* use default */ }
  }

  // ── Seeded entry (from an Ambient "Reflect" debrief) ──────────────────────
  // When the user taps "Talk it through" on a reflection, auto-start the
  // session and pre-fill the composer with a contextual opener so the
  // conversation continues seamlessly (the debrief summary is also already in
  // episodic memory server-side). Fully guarded by `seed` — with no seed this
  // is inert and the normal Begin-Session flow is byte-identical.
  useEffect(() => {
    if (!seed || seededRef.current || sessionId || sessionLoading) return
    seededRef.current = true
    ;(async () => {
      const id = await startSession()
      if (!id) { seededRef.current = false; return }
      setGreeting("Of course — I'm right here. Let's pick up where your reflection left off, whenever you're ready.")
      // Carry the ACTUAL material across, not just a topic line. The opener used
      // to be built from seed.summary alone while seed.reflection and
      // seed.debrief were handed over and silently dropped — so the session was
      // told roughly what the reflection was about but not what either of you
      // had said, and "why did you say that?" got a reconstruction instead of an
      // answer. Trimmed, because this is one message in a composer, not a file.
      const parts = []
      if (seed.summary)    parts.push(`We just did a reflection together about this: ${String(seed.summary).trim()}`)
      if (seed.debrief)    parts.push(`What I told you, in my words:\n"${String(seed.debrief).trim().slice(0, 1500)}"`)
      if (seed.reflection) parts.push(`What you reflected back to me:\n"${String(seed.reflection).trim().slice(0, 1000)}"`)
      parts.push("I'd like to keep working through it with you.")
      const opener = parts.length > 1
        ? parts.join('\n\n')
        : 'I’d like to talk through the reflection we just did together.'
      setInput(opener)
      setTimeout(() => textareaRef.current?.focus(), 50)
    })()
  }, [seed, sessionId, sessionLoading, startSession])

  // ── Screen Wake Lock ──────────────────────────────────────────────────────
  // Keep the screen awake during a session so the mic keeps capturing even
  // hands-free (e.g. while driving). Mobile browsers suspend the page when the
  // screen locks — that suspension is exactly what was dropping the conversation
  // and producing "Transcription failed". Holding a wake lock means the screen
  // stays on (it may dim) and the page — and the mic — keep running.
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
        wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null })
      }
    } catch { /* wake lock unavailable / denied — non-fatal */ }
  }, [])
  const releaseWakeLock = useCallback(async () => {
    try { await wakeLockRef.current?.release() } catch { /* already gone */ }
    wakeLockRef.current = null
  }, [])

  // ── Voice recording ───────────────────────────────────────────────────────
  const startRecording = async () => {
    setVoiceError(null)
    acquireWakeLock()                 // hold the screen on for the whole capture
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus'
        : 'audio/mp4'
      const recorder = new MediaRecorder(stream, { mimeType })
      chunksRef.current = []
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      // If the recorder is interrupted (OS reclaims the mic, brief glitch),
      // flush so the words captured so far aren't thrown away.
      recorder.onerror = () => { try { recorder.stop() } catch { /* noop */ } }
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
        if (!blob.size) { setTranscribing(false); return }   // nothing captured — stay quiet
        setTranscribing(true)
        try {
          const base64 = await new Promise((res, rej) => {
            const reader = new FileReader()
            reader.onload = () => res(reader.result.split(',')[1])
            reader.onerror = rej
            reader.readAsDataURL(blob)
          })
          const transcribe = () => api.post('/voice/transcribe', { audio: base64, mimeType: recorder.mimeType })
          let text
          // Retry once: if the screen just woke, the network may still be resuming.
          try { ({ text } = await transcribe()) }
          catch { await new Promise(r => setTimeout(r, 800)); ({ text } = await transcribe()) }
          // An empty result now means the server discarded a hallucinated
          // transcript (Whisper answers silence with "Thank you for watching").
          // Say so, rather than appearing to ignore someone who just spoke.
          if (text) setInput(prev => prev ? `${prev} ${text}` : text)
          else setVoiceError("I didn't catch that — the mic may not have picked you up. Try again, or type it.")
        } catch (err) {
          setVoiceError(err.message?.includes('not configured')
            ? 'Voice transcription requires OPENAI_API_KEY'
            : 'Could not transcribe that — tap the mic to try again, or type below.')
        } finally { setTranscribing(false) }
      }
      mediaRecorderRef.current = recorder
      recorder.start(1000)            // flush a chunk every second → interruption-resilient
      setRecording(true)
    } catch (err) {
      setVoiceError(err.name === 'NotAllowedError' ? 'Microphone permission denied.' : 'Could not access microphone.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) { mediaRecorderRef.current.stop(); setRecording(false) }
  }

  // Hold the wake lock for the whole open session; re-acquire when the user
  // returns to the tab (browsers drop it on hide). If the screen truly goes
  // away mid-recording, salvage what we captured rather than losing it.
  useEffect(() => {
    if (!sessionId) return
    acquireWakeLock()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquireWakeLock()
      else if (mediaRecorderRef.current && recording) stopRecording()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      releaseWakeLock()
    }
  }, [sessionId, recording, acquireWakeLock, releaseWakeLock])

  const handleEndSession = async () => {
    if (phase !== 'session' || ending) return
    setPhase('ending')
    setEnding(true)
    // Ask for the CLOSE, explicitly. The old wording ("reflect on what we
    // covered") was an open invitation to summarise, and with no closing
    // protocol behind it that came back as a clinical hand-off document
    // recommending a therapist. Now it asks for the thing a real session ends
    // with, and the server-side protocol defines what that means.
    await sendMessage(
      "I'd like to close this session now. Please read back what we covered today, " +
      'what came up underneath it, and two or three things for me to focus on before next time.'
    )
    setEnding(false)
  }

  const handleCompleteSession = async () => {
    ttsAudioRef.current?.pause()
    setPhase('complete')
    await endSession(messages)
    if (onSessionComplete) onSessionComplete()
  }

  // ── Couples: complete AND file this session in Reflections ───────────────
  // Two-tap on purpose: the transcript is SHARED material, so the second tap is
  // the explicit "we both agree" moment before it becomes a reflection in this
  // account's archive. The reflection is requested before the session closes
  // (the messages are already persisted server-side); failure is non-fatal —
  // the session still closes normally.
  const [reflectConfirm, setReflectConfirm] = useState(false)
  const [reflectSaving,  setReflectSaving]  = useState(false)
  const handleCompleteAndReflect = async () => {
    if (reflectSaving) return
    const sid = sessionId
    setReflectSaving(true)
    ttsAudioRef.current?.pause()
    try { await api.post('/ambient/reflect-session', { sessionId: sid }) }
    catch { /* non-fatal — closing the session matters more than the transfer */ }
    setPhase('complete')
    await endSession(messages)
    if (onSessionComplete) onSessionComplete()
  }

  // One turn from the room. In couples the text is labelled with who said it —
  // the companion needs that to address each partner by name and to keep the
  // transcript accurate, and the label is stripped again before display.
  const submitTurn = async (text, speaker = null) => {
    const body = text.trim()
    if (!body || loading) return
    if (mode === 'couples') turnsRef.current += 1
    const res = await sendMessage(speaker ? `[${speaker}] ${body}` : body)
    if (phase === 'ending') setPhase('completing')
    return res === null ? false : res   // false => caller restores the composer
  }

  const handleSend = async () => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')
    // A typed message in couples still belongs to whoever holds the floor.
    const result = await submitTurn(text, mode === 'couples' ? (floorHolder || couplesNames?.a) : null)
    // Put the words BACK if the send failed. Clearing the box before the request
    // resolves meant a dropped connection destroyed what someone had just found
    // the courage to write.
    if (result === false) setInput(prev => (prev ? prev : text))
  }

  // A spoken turn from the floor control: attributed, sent, never recorded.
  const handleCouplesTurn = (speaker, text) => {
    setFloorHolder(speaker)
    submitTurn(text, speaker)
  }

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const canEndSession = messages.length >= 2 && phase === 'session'
  const showComplete  = phase === 'completing' || (phase === 'ending' && !loading && messages.length > 0)

  // ── Pre-session start screen ──────────────────────────────────────────────
  if (!sessionId) {
    return (
      <div className="session-view">
        <div className="session-empty">
          {/* An interrupted conversation is OFFERED, never auto-restored. Being
              dropped straight back into something heavy without warning is its
              own harm — the person decides whether to pick it up. */}
          {recoverable && (
            <div style={ui.resumeCard}>
              <p style={ui.resumeLead}>
                We were in the middle of something{recoverable.messages?.length
                  ? ` — ${recoverable.messages.filter(m => m.role === 'user').length} ${recoverable.messages.filter(m => m.role === 'user').length === 1 ? 'thing you said' : 'things you said'} are still here.`
                  : '.'}
              </p>
              <p style={ui.resumeSub}>Nothing was lost when your screen went off.</p>
              <div style={ui.resumeRow}>
                <button style={ui.resumeGo} onClick={resumeSession}>Pick it back up</button>
                <button style={ui.resumeNo} onClick={discardRecoverable}>Start fresh</button>
              </div>
            </div>
          )}
          <div className="brand-mark">◎</div>
          <p style={{ fontWeight:600, fontSize:'1.05rem' }}>Your companion is ready.</p>
          {/* Who is this session for? Decided BEFORE anything is built or
              spoken. In couples mode the personalised greeting (private memory,
              last session) is never requested — a partner may already be here. */}
          {FEATURE_SESSION_MODES && (
            <div style={ui.modeBar} role="radiogroup" aria-label="Who is this session for?">
              {[['individual','🧍 Just me'], ['couples','💞 Couples']].map(([m, label]) => (
                <button key={m} role="radio" aria-checked={mode === m}
                  style={{ ...ui.modeBtn, ...(mode === m ? ui.modeBtnOn : {}) }}
                  disabled={sessionLoading || !canChangeMode({ sessionStarted: !!sessionId })}
                  onClick={() => setMode(m)}>{label}</button>
              ))}
            </div>
          )}
          <p style={{ fontSize: '0.875rem', color:'var(--mid-grey)', lineHeight:1.6, maxWidth:280, textAlign:'center' }}>
            {mode === 'couples'
              ? 'A guided space for your relationship — together with your partner, or just you. Nothing from your private sessions is brought into a couples session.'
              : 'SOVRANOVA remembers where you left off. Your session will be personal, evidence-based, and voice-first.'}
          </p>
          {sessionError && <p style={{ color: 'var(--crisis-red)', fontSize: '0.85rem', marginTop: '0.5rem' }}>{sessionError}</p>}
          <button className="start-session-btn" onClick={handleStartSession}
            disabled={sessionLoading} style={{ marginTop: '1.25rem' }}>
            {sessionLoading ? 'Starting...' : (mode === 'couples' ? 'Begin Couples Session' : 'Begin Session')}
          </button>
        </div>
      </div>
    )
  }

  // ── Active session ────────────────────────────────────────────────────────
  return (
    <div className="session-view">
      {/* Controls bar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
        {canEndSession ? (
          <button className="session-end-btn" onClick={handleEndSession} disabled={ending || loading}>
            {ending ? 'Closing...' : 'End Session'}
          </button>
        ) : <div />}
        {(!settings.textOnly && settings.voiceEnabled) ? (
          <button
            onClick={() => {
              voiceChoiceRef.current = true      // explicit choice — outranks the mode default
              setManualMute(m => !m)
              if (!manualMute) ttsAudioRef.current?.pause()
            }}
            style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:'1.1rem', color: manualMute ? '#c0392b' : 'var(--mid-grey)', padding:'0.25rem 0.5rem' }}
            title={manualMute
              ? (mode === 'couples' ? 'Silent — tap when you\'d like SOVRANOVA to speak' : 'Voice muted — tap to unmute')
              : 'Voice on — tap to mute'}
          >
            {manualMute ? '🔇' : '🔊'}
          </button>
        ) : <div />}
      </div>

      {FEATURE_SESSION_MODES && (
        <>
          <div style={ui.modeBar}>
            {/* The mode is FIXED for the life of a session (chosen on the entry
                screen). Switching mid-session moved private, memory-grounded
                replies into a shared room, or a partner's words into a private
                one. This is a label now, not a switch. */}
            <span style={{ ...ui.modeBtn, ...ui.modeBtnOn, cursor:'default' }} aria-label="Session mode"
              title={canChangeMode({ sessionStarted: !!sessionId }) ? undefined : 'To change mode, end this session and start another.'}>
              {mode === 'couples' ? '💞 Couples session' : '🧍 Individual session'}
            </span>
            {/* Sessions and Reflections are two halves of one loop — the
                conversation, then what's kept from it (David, 19 Aug). This
                pill NAVIGATES to the Reflections library; it is not a mode. */}
            {FEATURE_AMBIENT && onNavigate && (
              <button style={ui.modeBtn} onClick={() => onNavigate('ambient')}>🪞 Reflections</button>
            )}
          </div>
          {mode === 'couples' && (
            <p style={ui.modeCaption}>
              A guided space for your relationship — together with your partner, or just you.
              {couplesNames
                ? ' Tap whoever is speaking; your companion answers aloud once you\'ve both had the floor.'
                : ' Your companion will check who\'s here.'}
            </p>
          )}
        </>
      )}

      <div className="messages-area">
        {/* Companion greeting — personalised + voiced */}
        {messages.length === 0 && (
          <div className="message assistant">
            <MessageRenderer content={greeting || (mode === 'couples' ? COUPLES_ENTRANCE : INDIVIDUAL_DEFAULT_GREETING)} />
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id}
            className={`message ${msg.role}${
              crisis && msg.role === 'assistant' && msg === messages[messages.length - 1] ? ' crisis' : ''
            }`}>
            {msg.role === 'assistant' ? (
              <>
                <MessageRenderer content={msg.content} />
                {FEATURE_SESSION_MODES && msg.suggestions?.length > 0 && (
                  <div style={ui.chipRow}>
                    {msg.suggestions.map((sug, i) => (
                      <button key={i} style={ui.chip} onClick={() => onNavigate?.(sug.view)}>
                        {sug.label} →
                      </button>
                    ))}
                  </div>
                )}
                {/* Couples: anything delivered silently between turning points
                    is always one tap from being heard. */}
                {mode === 'couples' && !muted && !spokenIds.has(msg.id) && (
                  <button style={ui.sayAloud} onClick={() => sayAloud(msg)}>🔊 Say that aloud</button>
                )}
              </>
            ) : (
              <>
                {mode === 'couples' && TURN_LABEL.test(msg.content) && (
                  <span style={ui.speaker}>{msg.content.match(TURN_LABEL)[1]}</span>
                )}
                {msg.content.replace(TURN_LABEL, '')}
              </>
            )}
          </div>
        ))}

        {(phase === 'ending' || phase === 'completing') && !loading && (
          <div className="session-closing-divider"><span>Session Closing</span></div>
        )}
        {loading && <div className="message loading">...</div>}
        <div ref={bottomRef} />
      </div>

      {showComplete && !loading && (
        <div className="session-complete-bar">
          <p className="session-complete-hint">When you are ready to close this session:</p>
          <button className="session-complete-btn" onClick={handleCompleteSession} disabled={reflectSaving}>Complete Session ◎</button>
          {canOfferReflectionPromotion({ mode, promotionFlag: FEATURE_COUPLES_REFLECTION_PROMOTION }) && (
            !reflectConfirm ? (
              <button className="session-complete-btn" onClick={() => setReflectConfirm(true)} disabled={reflectSaving}>
                Complete &amp; save to Reflections 🪞
              </button>
            ) : (
              <button className="session-complete-btn" onClick={handleCompleteAndReflect} disabled={reflectSaving}>
                {reflectSaving ? 'Saving to Reflections…' : 'You both agree? Tap to confirm'}
              </button>
            )
          )}
        </div>
      )}

      {voiceError && <p style={{ textAlign:'center', fontSize:'0.8rem', color:'#c0392b', padding:'0.25rem 0' }}>{voiceError}</p>}
      {(recording || transcribing) && (
        <p style={{ textAlign:'center', fontSize:'0.8rem', color: recording ? '#c0392b' : '#888', padding:'0.25rem 0' }}>
          {recording ? '● Recording — tap to stop' : 'Transcribing...'}
        </p>
      )}

      {/* Individual stepped-care nudge — hidden in couples mode: the tier is
          derived from ONE user's private clinical signals and a partner may be
          watching the screen. The always-on Urgent Help control is unaffected. */}
      {mode !== 'couples' && <TalkToSomeone key={careTier} tier={careTier} exchanges={messages.length} />}

      {/* COUPLES: the floor control replaces the single mic. It asks for both
          first names and states plainly that voices are not recorded before it
          will open a microphone at all — that statement is the reason two
          people are willing to speak freely in front of a device, so it is not
          buried in settings. */}
      {mode === 'couples' && (phase === 'session' || phase === 'ending') && (
        // Before names are set the composer below is hidden, so the setup card
        // is the LAST element on screen — without clearance its "We're both
        // ready" button sits under the fixed AI-disclosure bar / Urgent-help
        // pill (seen live 14 Aug). The wrapper class reserves that space; once
        // names exist the composer's own clearance takes over.
        <div className={couplesNames ? undefined : 'session-clear-bottom'}>
          <CouplesFloor
            names={couplesNames}
            onSetup={(n) => { setCouplesNames(n); setFloorHolder(n.a) }}
            floorHolder={floorHolder}
            onFloor={setFloorHolder}
            onTurn={handleCouplesTurn}
            busy={loading}
          />
        </div>
      )}

      {(phase === 'session' || phase === 'ending') && (mode !== 'couples' || couplesNames) && (
        <div className="session-input-area">
          {phase === 'session' && mode !== 'couples' && (
            <button
              style={{ width:48, height:48, borderRadius:'50%', flexShrink:0, border:'none', cursor: transcribing ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', transition:'all 0.2s', background: recording ? '#c0392b' : transcribing ? 'var(--og-n200, #eee7db)' : 'var(--og-s200, #e1eecc)' }}
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing}
              title={recording ? 'Stop recording' : 'Click to speak'}>
              {transcribing ? '⏳' : recording ? '⏹' : '🎤'}
            </button>
          )}
          <textarea ref={textareaRef} className="session-input" value={input}
            onChange={e => { setInput(e.target.value); autoResize() }}
            onKeyDown={handleKey}
            placeholder={phase === 'ending' ? 'Share any final reflections...' : recording ? 'Recording...' : transcribing ? 'Transcribing...' : 'Type or speak...'}
            rows={1} disabled={loading || recording} />
          <button className="send-btn" onClick={handleSend} disabled={!input.trim() || loading}>↑</button>
        </div>
      )}
    </div>
  )
}
